<script setup>
import { computed } from 'vue'
import {useI18n} from "vue-i18n";
const {t} = useI18n();

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Array, null],
    default: null
  },
  options: {
    type: [Array, Object],
    required: true
  },
  label: {
    type: String,
    required: false
  },
  placeholder: {
    type: String,
    default: 'labels.select_value'
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  name: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: ''
  },
  ariaLabel: {
    type: String,
    default: 'Select'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  showPlaceholder: {
    type: Boolean,
    default: true
  },
  error: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'blur', 'focus'])

const selectClasses = computed(() => {
  const classes = [
    'form-select',
    `form-select-${props.size}`
  ]

  if (props.error) {
    classes.push('is-invalid')
  }

  return classes
})

const normalizedOptions = computed(() => {
  // Если объект {key: value}
  if (!Array.isArray(props.options)) {
    return Object.entries(props.options).map(([value, label]) => ({
      value,
      label
    }))
  }

  // Если массив объектов [{value, label}]
  return props.options
})

const handleChange = (event) => {
  const value = props.multiple
      ? Array.from(event.target.selectedOptions, option => option.value)
      : event.target.value || null

  emit('update:modelValue', value)
  emit('change', value, event)
}

const handleBlur = (event) => {
  emit('blur', event)
}

const handleFocus = (event) => {
  emit('focus', event)
}
</script>

<template>
  <div>
    <label v-if="label" :for="id" class="form-label">{{ label }}</label>
    <select
        :id="id"
        :name="name"
        :class="selectClasses"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :multiple="multiple"
        :aria-label="ariaLabel"
        :aria-describedby="error ? `${id}-error` : undefined"
        :aria-invalid="error ? 'true' : undefined"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
    >
      <option v-if="showPlaceholder && !multiple" value="" disabled>
        {{ t(placeholder) }}
      </option>
      <option
          v-for="option in normalizedOptions"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>

    <div v-if="error" :id="`${id}-error`" class="invalid-feedback">
      {{ error }}
    </div>
  </div>
</template>

<style scoped lang="scss">
</style>